import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { TimesheetState } from '../timesheet-collection.state';

/**
 * The checks that run before a timesheet is sent.
 *
 * Pure functions over the slice, so each is testable on its own and none of them needs the
 * `*Model` traversal the legacy `TimesheetAndExpensesComponent.isExceedingWorkHours` did. Whether a
 * failed check *blocks* the submit or merely warns is the page's decision, not theirs.
 */

/** Milliseconds in an hour. */
const MS_PER_HOUR = 3_600_000;

/**
 * Milliseconds of an ISO instant, or undefined when unset or unparsable.
 *
 * @param isoTimestamp the instant to parse
 * @returns the epoch milliseconds, or undefined
 */
const epoch = (isoTimestamp: string): number | undefined => {
    if (!isoTimestamp) {
        return undefined;
    }
    const parsed = Date.parse(isoTimestamp);
    return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Whether the timesheet claims to end after the moment it is being sent.
 *
 * A warning rather than a block in the legacy UI, and the same here: a technician filling in the
 * sheet slightly ahead of the clock is normal, but doing it by hours is usually a typo.
 *
 * @param timesheet the timesheet about to be sent
 * @param now the current instant as an ISO timestamp
 * @returns whether the end of work lies in the future
 */
export function isEndOfWorkInFuture(timesheet: TimesheetState, now: string): boolean {
    const endOfWork = epoch(timesheet.endOfWork);
    const current = epoch(now);
    return endOfWork !== undefined && current !== undefined && endOfWork > current;
}

/**
 * Whether the working time the timesheet accounts for exceeds the configured maximum, once pauses
 * and breaks are taken out.
 *
 * Port of the legacy `isExceedingWorkHours`, including its short circuit: the deductions are only
 * worth computing when the gross span already exceeds the limit. Pauses are the manual entries whose
 * task type the config lists as a pause — excluding the travel legs, whose kind is fixed and never a
 * pause — and breaks are the work entries carrying the break work code.
 *
 * @param timesheet the timesheet about to be sent
 * @param maxWorkingHours the configured limit; a negative value disables the check
 * @param pauseCodes the config's pause-code list, as the raw configured string
 * @returns whether the net working time exceeds the limit
 */
export function exceedsMaxWorkingHours(
    timesheet: TimesheetState,
    maxWorkingHours: number,
    pauseCodes: string,
): boolean {
    if (maxWorkingHours < 0) {
        return false;
    }

    const startOfWork = epoch(timesheet.startOfWork);
    const endOfWork = epoch(timesheet.endOfWork);
    if (startOfWork === undefined || endOfWork === undefined) {
        return false;
    }

    const grossHours = (endOfWork - startOfWork) / MS_PER_HOUR;
    if (grossHours <= maxWorkingHours) {
        return false;
    }

    const deducted = timesheet.entries.reduce((total, entry) => {
        const start = epoch(entry.start);
        const end = epoch(entry.end);
        if (start === undefined || end === undefined) {
            return total;
        }

        const isPause =
            entry.kind === 'manual' &&
            entry.travelType === undefined &&
            Boolean(entry.taskType) &&
            pauseCodes.includes(entry.taskType);
        const isBreak = entry.kind === 'work' && entry.workCode === TIMERECORDING_TYPE_OF_WORK_CODES.BREAK;

        return isPause || isBreak ? total + (end - start) : total;
    }, 0);

    return grossHours - deducted / MS_PER_HOUR > maxWorkingHours;
}

/**
 * Whether the timesheet still holds a confirmation that has not been submitted.
 *
 * This one *blocks*: the entries of an open confirmation are shown but not final, and sending the
 * timesheet would report times the confirmation may still change.
 *
 * @param timesheet the timesheet about to be sent
 * @returns whether any work entry belongs to a confirmation that is still open
 */
export function hasInProgressConfirmations(timesheet: TimesheetState): boolean {
    return timesheet.entries.some((entry) => entry.kind === 'work' && !entry.isConfirmationSubmitted);
}
